/**
 * Click a level's label to hide the category that draws it.
 *
 * Every category here is an EXISTING toggle, written through the same store
 * action the Levels panel / action bar uses, so it persists exactly as that
 * toggle does and switching it back on in the panel brings the level back.
 * Nothing in this file adds a setting.
 *
 * Labels are canvas-drawn by lightweight-charts (price-line axis tags and the
 * title pill just inside the pane) or by our primitives (Fair Price anchors,
 * the left-edge level labels), so the chart gets no DOM click target. Instead
 * ChartView lists the label boxes it knows of at click time and
 * `hitTestLabelTargets` picks the one under the pointer. Only a press that
 * did not move (`isClickNotDrag`) acts, so dragging the price scale to zoom
 * never hides anything.
 */
import { useLevelsStore, type LevelType } from '../../store/levelsStore';
import { useKeyLevelPrefsStore } from '../../store/profileLevelsStore';
import { useGexStore, GEX_ELEMENT_LABELS, type GexElement } from '../../store/gexStore';
import { useFairPriceStore } from '../../store/fairPriceStore';
import { LEVEL_CONFIGS } from '../levels-panel/levelConfigs';
import type { KeyLevelKind } from '../../utils/keyLevels';
import { MOC_LABEL } from '../../utils/mocLevel';
import { useToastStore } from '../../store/toastStore';
import { useSRStore, saveSRSettings, SR_TIMEFRAME_CONFIG } from '../../store/srStore';
import type { SRTimeframe } from '@shared/types';

const addToast = (t: Parameters<ReturnType<typeof useToastStore.getState>['addToast']>[0]) =>
  useToastStore.getState().addToast(t);

// ---------------------------------------------------------------- categories

/** One existing toggle a label can switch off. */
export type HideCategoryId =
  | `level:${LevelType}`
  | 'key:sessionProfile'
  | 'key:weeklyProfile'
  | `gex:${GexElement}`
  | 'fairPrice';

/** Key-level tag (right axis + pane title) -> the toggle that draws it. */
export const KEY_LEVEL_CATEGORY: Record<KeyLevelKind, HideCategoryId> = {
  poc: 'key:sessionProfile',
  vah: 'key:sessionProfile',
  val: 'key:sessionProfile',
  wpoc: 'key:weeklyProfile',
  wvah: 'key:weeklyProfile',
  wval: 'key:weeklyProfile',
  zeroGamma: 'gex:gammaFlip',
  putWall: 'gex:putWall',
  callWall: 'gex:callWall',
  ivUp: 'gex:ivBands',
  ivDown: 'gex:ivBands',
  onh: 'level:onhOnl',
  onl: 'level:onhOnl',
  // PDH/PDL/MOC are not drawn as key levels (only fed to confluence), but map
  // them anyway so the table is total.
  pdh: 'level:prevDayHodLod',
  pdl: 'level:prevDayHodLod',
  moc: 'level:mocOpen',
};

/**
 * Left-edge label text (PriceLevelLabelsPrimitive) -> its levelsStore toggle.
 * The texts are the ones ChartView hands the primitive.
 */
export const PRICE_LEVEL_LABEL_CATEGORY: Record<string, LevelType> = {
  HOD: 'sessionHodLod',
  LOD: 'sessionHodLod',
  'RTH-H': 'rthHodLod',
  'RTH-L': 'rthHodLod',
  PDH: 'prevDayHodLod',
  PDL: 'prevDayHodLod',
  'IB-H': 'ibRange',
  'IB-L': 'ibRange',
  MO: 'midnightOpen',
  PWH: 'prevWeekHodLod',
  PWL: 'prevWeekHodLod',
  CWH: 'currWeekHodLod',
  CWL: 'currWeekHodLod',
  PMH: 'prevMonthHodLod',
  PML: 'prevMonthHodLod',
  [MOC_LABEL]: 'mocOpen',
};

/**
 * Category for a left-edge label. The primitive merges labels closer than
 * 12px into "HOD/PDH"; that names two toggles, so it is not hideable (null)
 * unless both halves are the same toggle.
 */
export function categoryForPriceLevelLabel(label: string): HideCategoryId | null {
  const keys = new Set<LevelType>();
  for (const part of label.split('/')) {
    const k = PRICE_LEVEL_LABEL_CATEGORY[part.trim()];
    if (!k) return null;
    keys.add(k);
  }
  if (keys.size !== 1) return null;
  return `level:${[...keys][0]}`;
}

/** Name in the "Hidden <name>" toast: the same words as the panel row. */
export function categoryName(id: HideCategoryId): string {
  if (id === 'fairPrice') return 'Fair Price';
  if (id === 'key:sessionProfile') return 'POC / VAH / VAL';
  if (id === 'key:weeklyProfile') return 'Weekly POC / VAH / VAL';
  if (id.startsWith('gex:')) {
    const el = id.slice(4) as GexElement;
    return GEX_ELEMENT_LABELS[el]?.label ?? el;
  }
  const key = id.slice(6) as LevelType;
  return LEVEL_CONFIGS.find((c) => c.key === key)?.label ?? key;
}

/** Current state of the toggle. */
export function isCategoryOn(id: HideCategoryId): boolean {
  if (id === 'fairPrice') return useFairPriceStore.getState().enabled;
  if (id === 'key:sessionProfile') return useKeyLevelPrefsStore.getState().sessionProfile;
  if (id === 'key:weeklyProfile') return useKeyLevelPrefsStore.getState().weeklyProfile;
  if (id.startsWith('gex:')) return !!useGexStore.getState().visibility[id.slice(4) as GexElement];
  // Missing key = on: the chart draws a level whose key an older save lacks
  // (`levelVisibility.mocOpen ?? true`), so treat it the same here.
  return useLevelsStore.getState().visibility[id.slice(6) as LevelType] ?? true;
}

/**
 * Switch the toggle through the store action the Levels panel / action bar
 * uses (so it persists the same way). No-op when already in that state.
 */
export function setCategoryOn(id: HideCategoryId, on: boolean): void {
  if (isCategoryOn(id) === on) return;
  if (id === 'fairPrice') {
    useFairPriceStore.getState().setEnabled(on);
  } else if (id === 'key:sessionProfile' || id === 'key:weeklyProfile') {
    useKeyLevelPrefsStore.getState().toggle(id === 'key:sessionProfile' ? 'sessionProfile' : 'weeklyProfile');
  } else if (id.startsWith('gex:')) {
    useGexStore.getState().toggleElement(id.slice(4) as GexElement);
  } else {
    useLevelsStore.getState().setLevelVisible(id.slice(6) as LevelType, on);
  }
}

// ---------------------------------------------------------------- geometry

/**
 * A label box in chart-container CSS px. `category: null` marks something
 * drawn on top that is NOT hideable (the live price tag): a click on it must
 * not fall through to a level label underneath.
 */
export interface LabelTarget {
  left: number;
  right: number;
  top: number;
  bottom: number;
  category: HideCategoryId | null;
}

/**
 * Half the height of a lightweight-charts axis label / title pill: font size
 * plus 2.5/12 of it as padding above and below (PriceAxisRendererOptions).
 * One extra px so a click on the edge counts.
 */
export function axisLabelHalfHeight(fontSize: number): number {
  return (fontSize * (1 + 5 / 12)) / 2 + 1;
}


/** Axis label height (media px): font size + 2.5/12 of it above and below. */
export function axisLabelHeight(fontSize: number): number {
  return fontSize * (1 + 5 / 12);
}

/**
 * Where lightweight-charts (5.1, `alignLabels` on) actually paints each axis
 * tag. It does not draw a tag at its line's y when tags would collide: it
 * clamps tags at the pane edges, then pushes overlapping tags apart outward
 * from the centre label (the first series' first axis view, i.e. the candle
 * series' live price tag). This mirrors `PriceAxisWidget._fixLabelOverlap` +
 * `recalculateOverlapping` so the click box sits on the painted tag, not on
 * the raw price. `coords` are the raw line y's in the order lightweight-charts
 * collects them (the live price tag first, then price lines); returns the
 * painted y for each, same order.
 */
export function fixAxisTagOverlap(coords: readonly number[], center: number, scaleHeight: number, labelHeight: number): number[] {
  const render = coords.slice();
  const idx = coords.map((_, i) => i);
  const top = idx.filter((i) => coords[i] <= center).sort((a, b) => coords[b] - coords[a]);
  const bottom = idx.filter((i) => coords[i] > center);
  if (top.length && bottom.length) bottom.push(top[0]); // the centre label is shared
  bottom.sort((a, b) => coords[a] - coords[b]);
  const half = Math.floor(labelHeight / 2);
  for (const i of idx) {
    const c = coords[i];
    if (c > -half && c < half) render[i] = half;
    if (c > scaleHeight - half && c < scaleHeight + half) render[i] = scaleHeight - half;
  }
  const recalc = (views: number[], direction: 1 | -1) => {
    if (!views.length) return;
    let groupStart = 0;
    let space = direction === 1
      ? scaleHeight / 2 - (render[views[0]] - labelHeight / 2)
      : render[views[0]] - labelHeight / 2 - scaleHeight / 2;
    space = Math.max(0, space);
    for (let k = 1; k < views.length; k++) {
      const cur = render[views[k]];
      const prev = render[views[k - 1]];
      const overlap = direction === 1 ? cur > prev - labelHeight : cur < prev + labelHeight;
      if (overlap) {
        const next = prev - labelHeight * direction;
        render[views[k]] = next;
        const edge = next - (direction * labelHeight) / 2;
        const out = direction === 1 ? edge < 0 : edge > scaleHeight;
        if (out && space > 0) {
          const desired = direction === 1 ? -1 - edge : edge - scaleHeight;
          const shift = Math.min(desired, space);
          for (let m = groupStart; m < views.length; m++) render[views[m]] += direction * shift;
          space -= shift;
        }
      } else {
        groupStart = k;
        space = direction === 1 ? prev - labelHeight - cur : cur - (prev + labelHeight);
      }
    }
  };
  recalc(top, 1);
  recalc(bottom, -1);
  return render;
}

/** The price tag on the right price scale for a line at `y`. */
export function axisTagTarget(y: number, paneWidth: number, axisWidth: number, fontSize: number, category: HideCategoryId | null): LabelTarget {
  const h = axisLabelHalfHeight(fontSize);
  return { left: paneWidth, right: paneWidth + axisWidth, top: y - h, bottom: y + h, category };
}

/**
 * The title pill lightweight-charts draws inside the pane against the price
 * scale: border 1 + inner and outer padding (5/12 of the font each) + text +
 * tick (5).
 */
export function paneTitleTarget(y: number, paneWidth: number, textWidth: number, fontSize: number, category: HideCategoryId): LabelTarget {
  const h = axisLabelHalfHeight(fontSize);
  const w = Math.ceil(textWidth) + 1 + (fontSize / 12) * 10 + 5;
  return { left: paneWidth - w, right: paneWidth, top: y - h, bottom: y + h, category };
}

/**
 * A left-edge label from PriceLevelLabelsPrimitive: 10px text whose baseline
 * is 6px above the line, starting at x=5 with a 3px pad.
 */
export function leftLabelTarget(y: number, textWidth: number, category: HideCategoryId): LabelTarget {
  const baseline = y - 6;
  return { left: 2, right: 5 + textWidth + 3, top: baseline - 10 - 3, bottom: baseline + 3, category };
}

/**
 * The hideable category under (x, y), or null. Targets are listed bottom-most
 * first, so the LAST containing one is the one painted on top and wins; when
 * that is an occluder (category null) nothing is hit.
 */
export function hitTestLabelTargets(targets: readonly LabelTarget[], x: number, y: number): HideCategoryId | null {
  for (let i = targets.length - 1; i >= 0; i--) {
    const t = targets[i];
    if (x >= t.left && x <= t.right && y >= t.top && y <= t.bottom) return t.category;
  }
  return null;
}

// ---------------------------------------------------------------- hide + undo

/** How long the "Hidden ... Undo" toast stays up. */
export const HIDE_TOAST_MS = 5000;

/**
 * Switch a category off now and offer Undo on a toast (the shared toast
 * store). Undo switches the same toggle back on. Returns false when it was
 * already off (nothing to hide, no toast).
 */
export function hideCategoryWithUndo(id: HideCategoryId): boolean {
  if (!isCategoryOn(id)) return false;
  setCategoryOn(id, false);
  addToast({
    type: 'info',
    message: `Hidden ${categoryName(id)}`,
    duration: HIDE_TOAST_MS,
    action: { label: 'Undo', onClick: () => setCategoryOn(id, true) },
  });
  return true;
}

/**
 * A click on the price scale waits this long before hiding: a double-click
 * there resets autoscale and must not hide the tag under it.
 */
export const SCALE_DBLCLICK_WAIT_MS = 300;

/** Pointer movement (CSS px) above which a press is a drag, not a click. */
export const CLICK_SLOP_PX = 4;

/** A press is a click only when the pointer barely moved between down and up. */
export function isClickNotDrag(
  down: { x: number; y: number },
  up: { x: number; y: number },
  slop = CLICK_SLOP_PX,
): boolean {
  return Math.abs(up.x - down.x) <= slop && Math.abs(up.y - down.y) <= slop;
}

/**
 * S/R cluster labels are HTML (Chart/SRLabel.tsx) and a click already opens
 * their detail card, so the hide sits in that card. It switches off the
 * cluster's timeframe through the S/R panel's own toggle and save
 * (`toggleTimeframe` + `POST /sr-settings`), with the same Undo toast. The
 * panel never lets the last timeframe go, and neither does this.
 */
export function hideSRTimeframeWithUndo(tf: SRTimeframe): boolean {
  const { visibleTimeframes } = useSRStore.getState();
  if (!visibleTimeframes.has(tf)) return false;
  const name = `S/R ${SR_TIMEFRAME_CONFIG[tf].label} levels`;
  if (visibleTimeframes.size <= 1) {
    addToast({ type: 'warning', message: `Can't hide ${name}: S/R needs at least one timeframe on` });
    return false;
  }
  const save = () => {
    void saveSRSettings().then((ok) => {
      if (!ok) addToast({ type: 'error', message: 'Failed to save S/R settings' });
    });
  };
  useSRStore.getState().toggleTimeframe(tf);
  save();
  addToast({
    type: 'info',
    message: `Hidden ${name}`,
    duration: HIDE_TOAST_MS,
    action: {
      label: 'Undo',
      onClick: () => {
        if (useSRStore.getState().visibleTimeframes.has(tf)) return;
        useSRStore.getState().toggleTimeframe(tf);
        save();
      },
    },
  });
  return true;
}

// ---------------------------------------------------------------- DEV hook

/** A label a click would hide right now, at the client point that hits it. */
export interface ClickableLabel {
  category: HideCategoryId;
  clientX: number;
  clientY: number;
}

declare global {
  interface Window {
    /** DEV only: every chart's hideable labels (tests/level-label-hide.spec.ts). */
    __uttLevelLabels?: () => ClickableLabel[][];
  }
}

const exposedCharts: { el: HTMLElement; targets: () => LabelTarget[] }[] = [];

/**
 * Labels whose own centre resolves to themselves (i.e. not covered by a label
 * painted over them), in client coordinates.
 */
export function clickableLabels(targets: readonly LabelTarget[], originX: number, originY: number): ClickableLabel[] {
  const out: ClickableLabel[] = [];
  for (const t of targets) {
    if (!t.category) continue;
    const x = (t.left + t.right) / 2;
    const y = (t.top + t.bottom) / 2;
    if (hitTestLabelTargets(targets, x, y) !== t.category) continue;
    out.push({ category: t.category, clientX: originX + x, clientY: originY + y });
  }
  return out;
}

/** DEV only: publish a chart's label targets as `window.__uttLevelLabels()`. Returns the undo. */
export function exposeLevelLabels(el: HTMLElement, targets: () => LabelTarget[]): () => void {
  const entry = { el, targets };
  exposedCharts.push(entry);
  if (typeof window !== 'undefined') {
    window.__uttLevelLabels = () =>
      exposedCharts.map((e) => {
        const r = e.el.getBoundingClientRect();
        return clickableLabels(e.targets(), r.left, r.top);
      });
  }
  return () => {
    const i = exposedCharts.indexOf(entry);
    if (i >= 0) exposedCharts.splice(i, 1);
  };
}
